// cstToAstRefined.js
import * as T from '../dsl-engine/openfoam.tokens.js';

/**
 * Sort tokens by their position in the source text to preserve original order
 */
function sortTokensByPosition(tokens) {
  if (!tokens || tokens.length <= 1) return tokens;
  
  return [...tokens].sort((a, b) => {
    // Compare by line first, then by column position
    const lineComparison = (a.startLine || 0) - (b.startLine || 0);
    if (lineComparison !== 0) return lineComparison;
    return (a.startColumn || 0) - (b.startColumn || 0);
  });
}

export function cstToAst(cst) {
  if (!cst?.children) return { type: 'file', statements: [] };
  const statements = [];

  if (cst.children.statement) {
    for (const stmt of cst.children.statement) {
      statements.push(processStatement(stmt));
    }
  }

  if (cst.children.jinjaNode) {
    for (const node of cst.children.jinjaNode) {
      statements.push(processJinjaNode(node));
    }
  }

  return { type: 'file', statements };
}

function processStatement(stmt) {
  const c = stmt.children;
  if (c.vectorOrConnectivityList) return processVectorOrConnectivity(c.vectorOrConnectivityList[0]);
  if (c.namedDictList) return processNamedDictList(c.namedDictList[0]);
  if (c.namedStringList) return processNamedStringList(c.namedStringList[0]);
  if (c.functionObject) return processFunctionObject(c.functionObject[0]);
  if (c.dictionary) return processDictionary(c.dictionary[0]);
  if (c.macro) return processMacro(c.macro[0]);
  if (c.codestream) return processCodestream(c.codestream[0]);
  if (c.jinjaNode) return processJinjaNode(c.jinjaNode[0]);
  if (c.substitutionStatement) return processSubstitutionStatement(c.substitutionStatement[0]);
  if (c.keyValue) return processKeyValue(c.keyValue[0]);
  return { type: 'unknown' };
}

function processJinjaBody(body) {
  if (!body) return [];
  if (Array.isArray(body)) {
    return body.map(item => {
      // If it's already processed by the parser, return as-is
      if (item && typeof item === 'object' && item.type) {
        return item;
      }
      // Otherwise try to process it as CST
      if (item && item.children) {
        if (item.children.statement) {
          return processStatement(item.children.statement[0]);
        } else if (item.children.jinjaNode) {
          return processJinjaNode(item.children.jinjaNode[0]);
        }
      }
      return item;
    }).filter(item => item && item.type); // Filter out invalid items
  }
  // If body is a single object (CST), convert it to array and process
  if (body && typeof body === 'object' && body.children) {
    const statements = [];
    
    // Process all statements in the body
    if (body.children.statement) {
      for (const stmt of body.children.statement) {
        statements.push(processStatement(stmt));
      }
    }
    
    // Process all jinja nodes in the body
    if (body.children.jinjaNode) {
      for (const node of body.children.jinjaNode) {
        statements.push(processJinjaNode(node));
      }
    }
    
    return statements;
  }
  return [];
}

function processJinjaNode(node) {
  const c = node.children;
  
  // Handle Jinja expressions ({{ variable }})
  if (c.jinjaExpression) {
    const expr = c.jinjaExpression[0];
    
    // If the parser has already processed this and returned the object directly
    if (expr.type === 'jinja_expression') {
      return {
        type: 'jinja_expression',
        content: expr.content || '',
        variableName: expr.variableName || '',
        chunk: expr.content || ''
      };
    }
    
    // Fallback for CST processing
    const content = expr.content || expr.children?.JinjaExprChunk?.[0]?.image?.trim() || '';
    const variableName = content.split(/\s+/)[0]?.trim() || '';
    return { 
      type: 'jinja_expression', 
      content: content,
      expression: `{{ ${content} }}`, 
      variableName: variableName,
      chunk: content
    };
  }
  
  // Handle Jinja if statements ({% if condition %} ... {% endif %})
  if (c.jinjaIfStatement) {
    const ifStmt = c.jinjaIfStatement[0];
    
    // If the parser has already processed this and returned the object directly
    if (ifStmt.type === 'jinja_if_statement') {
      return {
        type: 'jinja_if_statement',
        branches: ifStmt.branches || []
      };
    }
    
    // Handle CST processing - extract from the CST structure
    const branches = [];
    const chunks = ifStmt.children?.JinjaStmtChunk || [];
    const bodies = ifStmt.children?.jinjaBody || [];
    const hasElif = ifStmt.children?.JinjaElifStart;
    const hasElse = ifStmt.children?.JinjaElseStart;
    
    let chunkIndex = 0;
    let bodyIndex = 0;
    
    // Process if branch
    if (ifStmt.children?.JinjaIfStart) {
      const ifCondition = chunks[chunkIndex]?.image?.trim() || '';
      const ifBody = bodies[bodyIndex] ? processJinjaBody(bodies[bodyIndex]) : [];
      
      branches.push({
        type: 'if',
        condition: ifCondition,
        chunk: ifCondition,
        body: ifBody
      });
      
      chunkIndex++;
      bodyIndex++;
    }
    
    // Process elif branches
    if (hasElif) {
      const elifCondition = chunks[chunkIndex]?.image?.trim() || '';
      const elifBody = bodies[bodyIndex] ? processJinjaBody(bodies[bodyIndex]) : [];
      
      branches.push({
        type: 'elif',
        condition: elifCondition,
        chunk: elifCondition,
        body: elifBody
      });
      
      chunkIndex++;
      bodyIndex++;
    }
    
    // Process else branch (if present)
    if (hasElse) {
      // Else doesn't have a condition chunk, but might have a body
      const elseBody = bodies[bodyIndex] ? processJinjaBody(bodies[bodyIndex]) : [];
      
      branches.push({
        type: 'else',
        condition: null,
        chunk: '',
        body: elseBody
      });
      
      bodyIndex++;
    }
    
    return {
      type: 'jinja_if_statement',
      branches: branches
    };
  }
  
  // Handle Jinja for statements ({% for item in items %} ... {% endfor %})
  if (c.jinjaForStatement) {
    const forStmt = c.jinjaForStatement[0];
    
    // If the parser has already processed this and returned the object directly
    if (forStmt.type === 'jinja_for_statement') {
      return {
        type: 'jinja_for_statement',
        loopVar: forStmt.loopVar || '',
        iterable: forStmt.iterable || '',
        chunk: `${forStmt.loopVar || ''} in ${forStmt.iterable || ''}`,
        body: processJinjaBody(forStmt.body)
      };
    }
    
    // Fallback for CST processing
    const body = forStmt.children?.jinjaBody ? processJinjaBody(forStmt.children.jinjaBody) : [];
    
    return {
      type: 'jinja_for_statement',
      loopVar: forStmt.loopVar || '',
      iterable: forStmt.iterable || '',
      chunk: `${forStmt.loopVar || ''} in ${forStmt.iterable || ''}`,
      body: body
    };
  }
  
  // Handle Jinja set statements ({% set variable = value %})
  if (c.jinjaSetStatement) {
    const setStmt = c.jinjaSetStatement[0];
    
    // If the parser has already processed this and returned the object directly
    if (setStmt.type === 'jinja_set_statement') {
      return {
        type: 'jinja_set_statement',
        variable: setStmt.variable || '',
        value: setStmt.value || null,
        chunk: `${setStmt.variable || ''} = ${setStmt.value || ''}`
      };
    }
    
    // Fallback for CST processing
    return {
      type: 'jinja_set_statement',
      variable: setStmt.variable || '',
      value: setStmt.value || null,
      chunk: `${setStmt.variable || ''} = ${setStmt.value || ''}`
    };
  }
  
  // Handle Jinja block statements ({% block name %} ... {% endblock %})
  if (c.jinjaBlockStatement) {
    const blockStmt = c.jinjaBlockStatement[0];
    
    // If the parser has already processed this and returned the object directly
    if (blockStmt.type === 'jinja_block_statement') {
      return {
        type: 'jinja_block_statement',
        blockName: blockStmt.blockName || '',
        chunk: blockStmt.blockName || '',
        body: processJinjaBody(blockStmt.body)
      };
    }
    
    // Fallback for CST processing
    const body = blockStmt.children?.jinjaBody ? processJinjaBody(blockStmt.children.jinjaBody) : [];
    
    return {
      type: 'jinja_block_statement',
      blockName: blockStmt.blockName || '',
      chunk: blockStmt.blockName || '',
      body: body
    };
  }
  
  // Handle Jinja extends statements ({% extends "template.html" %})
  if (c.jinjaExtendsStatement) {
    const extendsStmt = c.jinjaExtendsStatement[0];
    
    // If the parser has already processed this and returned the object directly
    if (extendsStmt.type === 'jinja_extends_statement') {
      return {
        type: 'jinja_extends_statement',
        template: extendsStmt.template || '',
        chunk: extendsStmt.template || ''
      };
    }
    
    // Fallback for CST processing
    return {
      type: 'jinja_extends_statement',
      template: extendsStmt.template || '',
      chunk: extendsStmt.template || ''
    };
  }
  
  // Handle Jinja include statements ({% include "template.html" %})
  if (c.jinjaIncludeStatement) {
    const includeStmt = c.jinjaIncludeStatement[0];
    
    // If the parser has already processed this and returned the object directly
    if (includeStmt.type === 'jinja_include_statement') {
      return {
        type: 'jinja_include_statement',
        template: includeStmt.template || '',
        chunk: includeStmt.template || ''
      };
    }
    
    // Fallback for CST processing
    return {
      type: 'jinja_include_statement',
      template: includeStmt.template || '',
      chunk: includeStmt.template || ''
    };
  }
  
  // Handle Jinja macro statements ({% macro name(params) %} ... {% endmacro %})
  if (c.jinjaMacroStatement) {
    const macroStmt = c.jinjaMacroStatement[0];
    
    // If the parser has already processed this and returned the object directly
    if (macroStmt.type === 'jinja_macro_statement') {
      const paramString = macroStmt.parameters?.join(', ') || '';
      const chunkContent = paramString ? `${macroStmt.macroName || ''}(${paramString})` : macroStmt.macroName || '';
      
      return {
        type: 'jinja_macro_statement',
        macroName: macroStmt.macroName || '',
        parameters: macroStmt.parameters || [],
        chunk: chunkContent,
        body: processJinjaBody(macroStmt.body)
      };
    }
    
    // Fallback for CST processing
    const body = macroStmt.children?.jinjaBody ? processJinjaBody(macroStmt.children.jinjaBody) : [];
    const paramString = macroStmt.parameters?.join(', ') || '';
    const chunkContent = paramString ? `${macroStmt.macroName || ''}(${paramString})` : macroStmt.macroName || '';
    
    return {
      type: 'jinja_macro_statement',
      macroName: macroStmt.macroName || '',
      parameters: macroStmt.parameters || [],
      chunk: chunkContent,
      body: body
    };
  }
  
  // Handle Jinja comments ({# comment #})
  if (c.jinjaComment) {
    const comment = c.jinjaComment[0];
    
    // If the parser has already processed this and returned the object directly
    if (comment.type === 'jinja_comment') {
      return {
        type: 'jinja_comment',
        comment: comment.value || '',
        value: comment.value || '',
        chunk: comment.value || ''
      };
    }
    
    // Fallback for CST processing
    const value = comment.children?.JinjaCommentChunk?.[0]?.image || comment.value || '';
    return { 
      type: 'jinja_comment', 
      comment: value,
      value: value,
      chunk: value
    };
  }
  
  // Handle legacy jinjaStatement for backward compatibility
  if (c.jinjaStatement) {
    const parts = c.jinjaStatement[0].children?.JinjaStmtPart?.map(tok => tok.image) || [];
    const content = parts.join(' ');
    return { 
      type: 'jinja_statement', 
      content: content,
      chunk: content
    };
  }
  
  return { type: 'jinja_unknown', chunk: '' };
}

function processDictionary(node) {
  const name = node.children.Name?.[0]?.image || (node.children.FoamFile ? 'FoamFile' : 'unnamed');
  const entries = (node.children.statement || []).map(processStatement);
  return { type: 'dictionary', name, entries };
}

function processKeyValue(node) {
  const key = node.children.Name?.[0]?.image || Object.keys(T.ReservedKeywords).find(k => node.children[k]);
  if (node.children.value) {
    return { type: 'keyValue', key, value: processValue(node.children.value[0]) };
  }
  if (node.children.jinjaExpression) {
    return processJinjaNode({ children: { jinjaExpression: node.children.jinjaExpression } });
  }
  return { type: 'keyValue', key, value: 'unknown' };
}

function processNamedDictList(node) {
  try {
    console.log("Processing namedDictList:", node);
    const name = node.children.Name?.[0]?.image || 'unnamed';
    console.log(`NamedDictList name: ${name}`);

    // Process each dictionary in the list
    const dictionaries = [];
    if (node.children.dictionary) {
      for (const dict of node.children.dictionary) {
        try {
          // Process the dictionary directly using processDictionary
          const processedDict = processDictionary(dict);
          if (processedDict) {
            dictionaries.push(processedDict);
          }
        } catch (error) {
          console.error(`Error processing dictionary in namedDictList: ${error.message}`);
          dictionaries.push({
            type: 'dictionary',
            name: 'error',
            error: error.message,
            entries: []
          });
        }
      }
    }

    // Validate that we have at least one dictionary
    if (dictionaries.length === 0) {
      console.warn(`NamedDictList '${name}' has no dictionaries`);
    }

    return {
      type: 'namedDictList',
      name,
      dictionaries
    };
  } catch (error) {
    console.error(`Error processing namedDictList: ${error.message}`);
    return {
      type: 'namedDictList',
      name: 'error',
      error: error.message,
      dictionaries: []
    };
  }
}

function processNamedStringList(node) {
  try {
    console.log("Processing namedStringList:", node);
    const name = node.children.Name?.[0]?.image || 'unnamed';
    console.log(`NamedStringList name: ${name}`);

    // Process strings from the list
    const strings = [];
    
    // Process StringLiteral tokens (quoted strings)
    if (node.children.StringLiteral) {
      for (const str of node.children.StringLiteral) {
        try {
          // Remove quotes from string literals
          const value = str.image.replace(/^"|"$/g, '');
          strings.push(value);
        } catch (error) {
          console.error(`Error processing string in namedStringList: ${error.message}`);
        }
      }
    }
    
    // Process Name tokens (unquoted strings)
    // Skip the first Name token as it's the list name
    if (node.children.Name && node.children.Name.length > 1) {
      for (let i = 1; i < node.children.Name.length; i++) {
        try {
          strings.push(node.children.Name[i].image);
        } catch (error) {
          console.error(`Error processing name as string in namedStringList: ${error.message}`);
        }
      }
    }

    // Validate that we have at least one string
    if (strings.length === 0) {
      console.warn(`NamedStringList '${name}' has no strings`);
    }

    return {
      type: 'namedStringList',
      name,
      strings
    };
  } catch (error) {
    console.error(`Error processing namedStringList: ${error.message}`);
    return {
      type: 'namedStringList',
      name: 'error',
      error: error.message,
      strings: []
    };
  }
}

function processFunctionObject(node) {
  const name = node.children.Name?.[0]?.image || 'unnamed';
  const functionType = node.children.Name?.[1]?.image || 'unknown';
  const entries = (node.children.statement || []).map(processStatement);
  return { type: 'functionObject', name, functionType, entries };
}

function processMacro(node) {
  const directive = Object.keys(node.children).find(k => k in T.ReservedKeywords) || 'unknown';
  const argument = node.children.RestOfLine?.[0]?.image.trim() || '';
  return { type: 'macro', directive, argument };
}

function processCodestream(node) {
  const content = node.children.CodeStreamChunk?.[0]?.image || '';
  return { type: 'codestream', content };
}

function processSubstitutionStatement(substitution) {
  // If the parser has already processed this and returned the object directly
  if (substitution.type === 'substitution') {
    return {
      type: 'substitution',
      value: substitution.value || '',
      variableName: substitution.variableName || ''
    };
  }
  
  // Fallback for CST processing
  const value = substitution.value || '';
  const variableName = substitution.variableName || value.replace(/^\$/, '') || '';
  
  return {
    type: 'substitution',
    value: value,
    variableName: variableName
  };
}

function processVectorOrConnectivity(node) {
  if (node.children.vector3list) return processVector3List(node.children.vector3list[0]);
  if (node.children.connectivity4list) return processConnectivity4List(node.children.connectivity4list[0]);
  return { type: 'vectorOrConnectivityList', value: null };
}

function processVector3List(node) {
  const name = node.children.Name?.[0]?.image || 'unnamed';
  const vectors = (node.children.vector3 || []).map(v => {
    try {
      // Get numbers from either FloatLiteral or IntegerLiteral
      const floatNums = (v.children.FloatLiteral || []).map(n => Number(n.image));
      const intNums = (v.children.IntegerLiteral || []).map(n => Number(n.image));
      const nums = [...floatNums, ...intNums];
      
      // Validate we have exactly 3 components
      if (nums.length !== 3) {
        throw new Error(`Invalid vector3: expected 3 components, got ${nums.length}`);
      }
      
      return { type: 'vector3', components: nums };
    } catch (error) {
      console.error(`Error processing vector3: ${error.message}`);
      return { type: 'vector3', error: error.message, components: [0, 0, 0] };
    }
  });
  return { type: 'vector3list', name, vectors };
}

function processConnectivity4List(node) {
  const name = node.children.Name?.[0]?.image || 'unnamed';
  const connections = (node.children.connectivity4 || []).map(c => {
    try {
      // Connectivity4 should only have IntegerLiteral
      if (!c.children.IntegerLiteral) {
        throw new Error('Connectivity4 must contain only integer literals');
      }
      
      const nums = c.children.IntegerLiteral.map(n => {
        const value = Number(n.image);
        // Validate that the number is a non-negative integer
        if (!Number.isInteger(value) || value < 0) {
          throw new Error(`Invalid connectivity4 index: expected non-negative integer, got ${value}`);
        }
        return value;
      });
      
      // Validate we have exactly 4 indices
      if (nums.length !== 4) {
        throw new Error(`Invalid connectivity4: expected 4 indices, got ${nums.length}`);
      }
      
      return { type: 'connectivity4', indices: nums };
    } catch (error) {
      console.error(`Error processing connectivity4: ${error.message}`);
      return { type: 'connectivity4', error: error.message, indices: [0, 0, 0, 0] };
    }
  });
  return { type: 'connectivity4list', name, connections };
}

function processValue(node) {
  const c = node.children;
  
  // If this is already processed by the enhanced parser and contains a valueList
  if (node.type === 'valueList') {
    return {
      type: 'valueList',
      values: node.values || []
    };
  }
  
  if (c.jinjaExpression) return processJinjaNode({ children: { jinjaExpression: c.jinjaExpression } });
  if (c.dictionary) return processDictionary(c.dictionary[0]);
  if (c.fieldvalue) {
    const fv = c.fieldvalue[0];
    if (fv.children.Uniform) return { type: 'uniform', value: processValue(fv.children.value[0]) };
    if (fv.children.NonUniform) {
      const type = fv.children.Name?.[0]?.image || '';
      const value = fv.children.list ? processValue({ children: { list: fv.children.list } }) : fv.children.BinaryBlob?.[0]?.image || '';
      return { type: 'nonuniform', valueType: type, value };
    }
  }
  
  // Handle multiple numeric values (FloatLiteral or IntegerLiteral)
  const hasMultipleFloats = c.FloatLiteral && c.FloatLiteral.length > 1;
  const hasMultipleInts = c.IntegerLiteral && c.IntegerLiteral.length > 1;
  const hasMixedNumericAndNames = (c.FloatLiteral || c.IntegerLiteral) && c.Name;
  
  if (hasMultipleFloats || hasMultipleInts || hasMixedNumericAndNames) {
    // Collect all tokens (names, floats, integers) and sort by position
    const allTokens = [
      ...(c.Name || []),
      ...(c.IntegerLiteral || []),
      ...(c.FloatLiteral || []),
      ...(c.StringLiteral || [])
    ];
    
    const sortedTokens = sortTokensByPosition(allTokens);
    
    const values = sortedTokens.map(token => {
      // Check token type and convert accordingly
      if (token.tokenType && token.tokenType.name === 'IntegerLiteral') {
        return Number(token.image);
      } else if (token.tokenType && token.tokenType.name === 'FloatLiteral') {
        return Number(token.image);
      } else if (token.tokenType && token.tokenType.name === 'StringLiteral') {
        return token.image.replace(/^"|"$/g, '');
      } else {
        // Name or other token
        return token.image;
      }
    });
    
    return {
      type: 'valueList',
      values: values
    };
  }
  
  // Handle single numeric values
  if (c.IntegerLiteral || c.FloatLiteral) return Number((c.IntegerLiteral?.[0] || c.FloatLiteral?.[0]).image);
  if (c.StringLiteral) return c.StringLiteral[0].image.replace(/^"|"$/g, '');
  if (c.Substitution) {
    const v = c.Substitution[0].image;
    return { type: 'jinja_value', expression: `{{ ${v.slice(1)} }}`, variableName: v.slice(1) };
  }
  
  // Handle multiple names - return as valueList instead of nameList
  if (c.Name?.length > 1) {
    // Sort Name tokens by their position in the source text to preserve original order
    const sortedNames = sortTokensByPosition(c.Name);
    
    return { 
      type: 'valueList', 
      values: sortedNames.map(n => n.image) 
    };
  }
  
  // Handle function calls in values - check if it's a function call pattern
  if (c.functionCall) {
    const fc = c.functionCall[0];
    const name = fc.children.Name?.[0]?.image || 'unknown';
    const args = (fc.children.Name?.slice(1) || []).map(n => n.image);
    return {
      type: 'functionCall',
      name: name,
      arguments: args
    };
  }
  
  if (c.Name?.length === 1) return c.Name[0].image;
  if (c.symbolicDimension) {
    const d = c.symbolicDimension[0].children;
    // Collect all tokens and sort by position to preserve original order
    const allTokens = [...(d.Name || []), ...(d.IntegerLiteral || []), ...(d.FloatLiteral || []), ...(d.Caret || []), ...(d.Mul || []), ...(d.Div || [])];
    const sortedTokens = sortTokensByPosition(allTokens);
    return { type: 'symbolicDimension', values: sortedTokens.map(t => t.image) };
  }
  if (c.list) return { type: 'list', items: (c.list[0].children.listItem || []).map(processValue) };
  if (c.prelist) {
    const size = Number(c.prelist[0].children.IntegerLiteral[0].image);
    const content = c.prelist[0].children.list ? processValue({ children: { list: c.prelist[0].children.list } }) : c.prelist[0].children.BinaryBlob?.[0]?.image;
    return { type: 'prelist', size, content };
  }
  if (c.vector3) {
    try {
      // Get all numeric tokens and sort by position to preserve original order
      const floatTokens = c.vector3[0]?.children?.FloatLiteral || [];
      const intTokens = c.vector3[0]?.children?.IntegerLiteral || [];
      const allNumericTokens = [...floatTokens, ...intTokens];
      
      const sortedTokens = sortTokensByPosition(allNumericTokens);
      const nums = sortedTokens.map(n => Number(n.image));
      
      // Validate we have exactly 3 components
      if (nums.length !== 3) {
        throw new Error(`Invalid vector3: expected 3 components, got ${nums.length}`);
      }
      
      return { type: 'vector3', components: nums };
    } catch (error) {
      console.error(`Error processing vector3: ${error.message}`);
      return { type: 'vector3', error: error.message, components: [0, 0, 0] };
    }    
  }
  if (c.connectivity4) {
    try {
      // Connectivity4 should only have IntegerLiteral
      if (!c.connectivity4[0].children.IntegerLiteral) {
        throw new Error('Connectivity4 must contain only integer literals');
      }
      
      // Sort integer tokens by position to preserve original order
      const intTokens = c.connectivity4[0].children.IntegerLiteral;
      const sortedTokens = sortTokensByPosition(intTokens);
      
      const nums = sortedTokens.map(n => {
        const value = Number(n.image);
        // Validate that the number is a non-negative integer
        if (!Number.isInteger(value) || value < 0) {
          throw new Error(`Invalid connectivity4 index: expected non-negative integer, got ${value}`);
        }
        return value;
      });
      
      // Validate we have exactly 4 indices
      if (nums.length !== 4) {
        throw new Error(`Invalid connectivity4: expected 4 indices, got ${nums.length}`);
      }
      
      return { type: 'connectivity4', indices: nums };
    }
    catch (error) {
      console.error(`Error processing connectivity4: ${error.message}`);
      return { type: 'connectivity4', error: error.message, indices: [0, 0, 0, 0] };
    }
  }

  return 'unknown_value';
}
